import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";

import WithAppLayout from "../../../components/WithAppLayout";
import { LoadingIndicator } from "../../../components/LoadingIndicator";
import JobsPageHeader from "../../../components/JobsPageHeader";
import JobSyncStatus from "../../../components/JobSyncStatus";
import ChangeJobOwnerModal from "../../../components/ChangeJobOwnerModal";

import { GET_PAGINATED_JOBS_QUERY } from "../../../graphql/agencies";
import { UPDATE_MOBLYZE_JOB_STATUS_MUTATION } from "../../../graphql/jobs_admin";
import { getLocalStorageItem } from "../../../helpers/utils";
import {
  Text,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  HStack,
  useToast,
  Tooltip,
  Box,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/icons";
import { FiMoreHorizontal, FiEdit2, FiTrash, FiUser } from "react-icons/fi";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import PaginationTable from "../../../components/PaginationTable/PaginationTable";
import { COLUMN_PRIORITY } from "../../../components/PageTableWithSearch/tableDataHelper";
import { createColumnHelper } from "@tanstack/react-table";
import { PREVIEW_JOB_PATH } from "../../../routes";

const columnHelper = createColumnHelper();

// Where a pending job came from. Bullhorn-sourced jobs carry the numeric
// job-order id; scraped careers-site jobs carry a URL or nothing at all.
export function jobSourceLabel(bullhornId) {
  if (bullhornId && /^\d+$/.test(String(bullhornId).trim())) return "Bullhorn";
  return "Careers site";
}

function PendingJobsInner({ currentUser, loading }) {
  const LS_KEY = "pending-jobs-filters";
  const LS_PAGE_KEY = "pending-jobs-pagination";
  const localStorage = window.localStorage;
  const toast = useToast();
  const navigate = useNavigate();

  const defaultFilters = getLocalStorageItem(LS_KEY, {
    search: "",
    status: "Pending",
  });
  const [filters, setFilters] = useState(defaultFilters);
  const defaultPagination = getLocalStorageItem(LS_PAGE_KEY, { pageIndex: 0, pageSize: 10 });
  const [pagination, setPagination] = useState(defaultPagination);
  // The job whose owner is being changed, if any.
  const [ownerJobId, setOwnerJobId] = useState(null);

  // Mutation for updating job status
  const [updateJobStatus, { loading: isUpdating }] = useMutation(UPDATE_MOBLYZE_JOB_STATUS_MUTATION, {
    onCompleted: (data) => {
      const updatedJob = data.updateMoblyzeJobStatus;
      const statusText = updatedJob.status === "Published" ? "approved and posted" : "deleted";
      toast({
        title: "Job updated successfully",
        description: `Job "${updatedJob.title}" has been ${statusText}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Refetch the table data to reflect changes
      window.location.reload();
    },
    onError: (error) => {
      toast({
        title: "Failed to update job",
        description: error.message || "An error occurred while updating the job status.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  });

  const setFiltersAndSave = useCallback((patchFilters) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, ...patchFilters };
      window.localStorage.setItem(LS_KEY, JSON.stringify(newFilters));
      return newFilters;
    });
  }, []);

  const savePagination = (data) => {
    data && localStorage.setItem(LS_PAGE_KEY, JSON.stringify(data));
  };

  const [debouncedSearch] = useState(filters.search);

  // Handler for approving and posting job
  const handleApproveAndPost = async (jobId) => {
    try {
      await updateJobStatus({
        variables: {
          moblyzeJobId: jobId,
          status: "Published"
        }
      });
    } catch (error) {
      console.error('Error approving job:', error);
    }
  };

  // Handler for deleting job
  const handleDeleteJob = async (jobId) => {
    try {
      await updateJobStatus({
        variables: {
          moblyzeJobId: jobId,
          status: "Deleted"
        }
      });
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setFiltersAndSave({ search: debouncedSearch });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearch, setFiltersAndSave]);

  if (loading) {
    return <LoadingIndicator />;
  }

  const tableColumns = [
    columnHelper.accessor("category", {
      header: "Job title",
      meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
    }),
    columnHelper.accessor("issues", {
      cell: (info) => {
        const issues = info.getValue();
        const count = issues?.length ? issues.length : 0;

        // Create tooltip content showing all issues
        const tooltipContent = count > 0 ? (
          <Box maxW="300px">
            <Text fontSize="sm" fontWeight="bold" mb={2}>Issues found:</Text>
            {issues.map((issue, index) => (
              <Text key={index} fontSize="xs" mb={1}>
                • {issue}
              </Text>
            ))}
          </Box>
        ) : (
          <Text fontSize="sm">No issues found</Text>
        );

        return (
          <Tooltip
            label={tooltipContent}
            placement="top"
            hasArrow
            bg="gray.800"
            color="white"
            borderRadius="md"
            p={3}
            isDisabled={count === 0}
          >
            <Text
              borderRadius="6px"
              p="0 8px"
              display="inline-block"
              h="20px"
              bgColor={count > 0 ? "orange.800" : "teal.800"}
              color={count > 0 ? "orange.100" : "teal.100"}
              cursor={count > 0 ? "help" : "default"}
              _hover={count > 0 ? { bgColor: "orange.700" } : {}}
            >
              {`${count} issue${count !== 1 ? 's' : ''}`}
            </Text>
          </Tooltip>
        );
      },
      header: "Issues",
      meta: { priority: COLUMN_PRIORITY.IMPORTANT },
    }),
    columnHelper.accessor("department", {
      cell: (info) => {
        const department = info.getValue();
        return department?.name;
      },
      header: "Department",
      meta: { priority: COLUMN_PRIORITY.SECONDARY },
    }),
    columnHelper.accessor("location", {
      header: "Location",
      meta: { priority: COLUMN_PRIORITY.SECONDARY },
    }),
    columnHelper.accessor("bullhornId", {
      cell: (info) => jobSourceLabel(info.getValue()),
      header: "Source",
      meta: { priority: COLUMN_PRIORITY.OPTIONAL },
    }),
    columnHelper.accessor("createdAt", {
      cell: (info) => {
        const date = info.getValue();
        return new Date(date).toLocaleDateString();
      },
      header: "Date Synced",
      meta: { priority: COLUMN_PRIORITY.OPTIONAL },
    }),
    columnHelper.accessor("id", {
      cell: (info) => {
        const id = info.getValue();
        const issues = info?.row?.original?.issues;
        return (
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<FiMoreHorizontal color="white" />}
              variant="dark"
              size="sm"
            />
            <MenuList border="1px" borderColor="gray.700" borderRadius="8px" p="5px">
              <MenuItem onClick={() => navigate(PREVIEW_JOB_PATH.replace(":id", id))}>
                <HStack>
                  <Icon as={FiEdit2} />
                  <Text>Review Details</Text>
                </HStack>
              </MenuItem>
              <MenuItem onClick={() => setOwnerJobId(id)}>
                <HStack>
                  <Icon as={FiUser} />
                  <Text>Change Owner</Text>
                </HStack>
              </MenuItem>
              {!issues?.length && (
              <MenuItem onClick={() => handleApproveAndPost(id)} disabled={isUpdating}>
                <HStack>
                  <Icon as={HiOutlineBadgeCheck} />
                  <Text>Approve & Post</Text>
                </HStack>
              </MenuItem>
              )}
              <MenuItem color="red.300" onClick={() => handleDeleteJob(id)} disabled={isUpdating}>
                <HStack>
                  <Icon as={FiTrash} />
                  <Text>Delete Job</Text>
                </HStack>
              </MenuItem>
            </MenuList>
          </Menu>
        );
      },
      header: "Actions",
      // The row's only affordance, so it stays on screen at every width and
      // cannot be hidden from the Columns menu.
      enableHiding: false,
      meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
    }),
  ];

  return (
    <Stack>
      <JobsPageHeader title="Pending" />
      <JobSyncStatus currentUser={currentUser} />
      <PaginationTable
        title={null}
        tableId="agency-pending-jobs"
        columns={tableColumns}
        query={GET_PAGINATED_JOBS_QUERY}
        queryName="paginatedAgencyJobs"
        itemKey="jobs"
        filters={filters}
        paginationState={[pagination, setPagination, savePagination]}
        filterInputs={null}
      />
      <ChangeJobOwnerModal jobId={ownerJobId} onClose={() => setOwnerJobId(null)} />
    </Stack>
  );
}

export { PendingJobsInner };
export default WithAppLayout(PendingJobsInner);
